import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Building2, CarFront, CircuitBoard, ClipboardCheck, CookingPot, Factory, Heater, Lamp, Lightbulb, MessageCircle, PlugZap, Route, ShieldCheck, Siren, Wrench,
  Ruler, Timer, MapPinned, FileCheck2,
} from "lucide-react";
import { COMPANY, IMG, PRICE_GROUPS, PRICE_NOTE, PRICES } from "@/lib/content";
import { EASE, Reveal } from "@/components/public/Reveal";
import { Btn, Eyebrow, Photo, TextLink } from "@/components/public/ui";
import { cn } from "@/lib/utils";

const ICON = { strokeWidth: 1.5, absoluteStrokeWidth: true, "aria-hidden": true };
const PRICE_ICONS = {
  heimeladar: PlugZap,
  "lading-sameige": CarFront,
  sikringsskap: CircuitBoard,
  "el-kontroll": ShieldCheck,
  komfyrvakt: CookingPot,
  varmekablar: Heater,
  downlights: Lightbulb,
  utelys: Lamp,
  servicebesok: Wrench,
  vakt: Siren,
  serviceavtale: ClipboardCheck,
};

const service = PRICES.find((p) => p.id === "servicebesok");

/* How prices work: grounded in the price list and the process copy already on the site. */
const HOW = [
  { icon: Ruler, title: "Fastpris på standardjobbar", text: "Dei vanlegaste jobbane har fast pris når anlegget er som forventa. Vi avklarer kva som er standard før du bestiller." },
  { icon: Timer, title: "Timepris når omfanget er ukjent", text: `Feilsøking og mindre arbeid blir fakturert per time: ${service?.price || "1 490 kr"} for første time, deretter 1 190 kr per time. Rapport på e-post etter besøket.` },
  { icon: MapPinned, title: "Synfaring og tilbod på større arbeid", text: "Rehabilitering, hytte, næringsbygg, industri og samferdsel blir prisa etter synfaring og skriftleg tilbod." },
  { icon: FileCheck2, title: "Dokumentasjon er med", text: "Når arbeidet er ferdig, får du nødvendig dokumentasjon og samsvarserklæring der det er krav om det." },
];

const FACTORS = [
  ["Avstand", "frå sikringsskap til der utstyret skal stå"],
  ["Kapasitet", "i det eksisterande anlegget og skapet"],
  ["Tilkomst", "kjellar, loft, skjult eller open forlegging"],
  ["Utstyr", "val av ladar, armatur eller styring"],
];

const LARGE = [
  { icon: Building2, title: "Bygg og næring", text: "Komplett elektroinstallasjon i nye og eksisterande næringsbygg.", market: "bygg_naering" },
  { icon: Factory, title: "Industri", text: "Elektro, automasjon og vedlikehald på prosess- og industrianlegg.", market: "industri" },
  { icon: Route, title: "Samferdsel", text: "Tunnelteknikk, styring og vegbelysning.", market: "samferdsel" },
];

const PriceItem = ({ item, index }) => {
  const Icon = PRICE_ICONS[item.id] || Wrench;
  return (
    <Reveal as="li" delay={Math.min(index, 4) * 0.04} className="border-b border-border" data-testid={`price-row-${item.id}`}>
      <Link
        to={`/kontakt?teneste=${encodeURIComponent(item.id)}`}
        className="group -mx-4 grid grid-cols-1 gap-y-5 rounded-2xl px-4 py-6 transition-colors duration-200 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:-mx-6 sm:grid-cols-[3rem_minmax(0,1fr)_auto] sm:items-center sm:gap-x-8 sm:px-6 sm:py-7"
        data-testid={`price-order-${item.id}`}
      >
        <Icon className="hidden h-7 w-7 text-foreground/55 transition-colors duration-300 group-hover:text-brand sm:block" {...ICON} />
        <span className="min-w-0">
          <span className="block text-[17px] font-medium leading-snug sm:text-[18px]">{item.name}</span>
          <span className="mt-1.5 block max-w-[52ch] text-[14.5px] leading-relaxed text-muted-foreground">{item.desc}</span>
        </span>
        <span className="flex flex-wrap items-end justify-between gap-x-6 gap-y-4 sm:flex-col sm:items-end sm:justify-start">
          <span className="sm:text-right">
            <span className="t-num block text-[1.6rem] leading-none sm:text-[2rem]">{item.price}</span>
            <span className="mt-2 inline-flex items-center gap-1.5 text-[12.5px] font-medium text-muted-foreground">
              <span className="h-1.5 w-1.5 rounded-full bg-dot" aria-hidden />
              {item.unit}
            </span>
          </span>
          <span className="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-full border border-border bg-background px-4 text-[13.5px] font-medium text-foreground transition-[background-color,border-color,color] duration-300 group-hover:border-brand group-hover:bg-brand group-hover:text-on-brand">
            {item.price.startsWith("Etter") ? "Be om synfaring" : "Bestill"}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" strokeWidth={2} aria-hidden />
          </span>
        </span>
      </Link>
    </Reveal>
  );
};

export default function Prices() {
  const reduce = useReducedMotion();
  const [active, setActive] = useState(PRICE_GROUPS[0].id);
  const sectionRefs = useRef({});
  const total = PRICES.length;

  /* Scroll-spy: the group closest to the upper third of the viewport is active. */
  useEffect(() => {
    const ids = [...PRICE_GROUPS.map((g) => g.id), "slik", "storre"];
    const onScroll = () => {
      const line = window.innerHeight * 0.32;
      let best = ids[0];
      let bestDist = Infinity;
      for (const id of ids) {
        const el = sectionRefs.current[id];
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (r.top <= line && r.bottom > line) { best = id; bestDist = 0; break; }
        const d = Math.abs(r.top - line);
        if (d < bestDist) { bestDist = d; best = id; }
      }
      setActive(best);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);

  const jump = (id) => (e) => {
    e.preventDefault();
    const el = sectionRefs.current[id];
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top, behavior: reduce ? "instant" : "smooth" });
    window.history.replaceState(null, "", `#${id}`);
  };

  const openChat = () => window.dispatchEvent(new CustomEvent("ke:open-chat"));

  const RAIL = [...PRICE_GROUPS.map((g) => ({ id: g.id, label: g.title, count: g.items.length })), { id: "slik", label: "Slik set vi pris" }, { id: "storre", label: "Større jobbar" }];

  return (
    <div data-testid="public-prices-page" className="overflow-x-clip">
      {/* ---------- Head ---------- */}
      <section className="container-public pt-10 sm:pt-14 lg:pt-20" data-testid="public-page-head">
        <div className="grid gap-8 lg:grid-cols-12 lg:gap-12">
          <motion.div className="lg:col-span-8" initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
            <p className="t-mono flex items-center gap-3"><span>Prisar</span><span aria-hidden className="h-px w-8 bg-border" /><span>{total} priseksempel</span></p>
            <h1 className="t-h1 mt-5 max-w-[16ch]">
              Pris på vanlege oppdrag.
              <span className="block text-muted-foreground/70">Avklart før vi startar.</span>
            </h1>
          </motion.div>
          <motion.div className="flex flex-col justify-end gap-6 lg:col-span-4" initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE, delay: 0.1 }}>
            <p className="t-lead max-w-md">Døme på faste prisar for dei vanlegaste jobbane heime og i mindre bedrifter. Industri, samferdsel og større prosjekt blir prisa etter synfaring og skriftleg tilbod.</p>
            <div className="flex flex-wrap items-center gap-3">
              <Btn to="/kontakt" size="lg" arrow data-testid="public-prices-cta">Be om tilbod</Btn>
              <Btn variant="ghost" size="lg" onClick={openChat} data-testid="public-prices-chat-button"><MessageCircle className="h-4 w-4" />Spør om pris</Btn>
            </div>
          </motion.div>
        </div>
        <p className="caption mt-8 max-w-2xl" data-testid="public-prices-demo-note">{PRICE_NOTE}</p>
      </section>

      {/* ---------- Rail + groups ---------- */}
      <section className="container-public mt-12 grid gap-10 sm:mt-16 lg:grid-cols-12 lg:gap-16" data-testid="public-prices-list">
        {/* Rail */}
        <aside className="min-w-0 lg:col-span-3">
          <nav aria-label="Prisgrupper" className="lg:sticky lg:top-24" data-testid="public-prices-rail">
            <ol className="-mx-5 flex gap-1 overflow-x-auto px-5 scrollbar-none sm:-mx-8 sm:px-8 lg:mx-0 lg:block lg:space-y-0.5 lg:overflow-visible lg:px-0 lg:border-l lg:border-border">
              {RAIL.map((r) => {
                const on = active === r.id;
                return (
                  <li key={r.id} className="shrink-0">
                    <a
                      href={`#${r.id}`}
                      onClick={jump(r.id)}
                      aria-current={on ? "true" : undefined}
                      data-testid={`public-prices-rail-${r.id}`}
                      className={cn(
                        "relative flex h-10 items-center gap-3 whitespace-nowrap rounded-full border px-4 text-[14px] font-medium transition-colors duration-200 lg:h-auto lg:rounded-none lg:border-0 lg:py-2 lg:pl-5 lg:pr-0",
                        on ? "border-foreground bg-foreground text-background lg:bg-transparent lg:text-foreground" : "border-border text-muted-foreground hover:border-foreground/50 hover:text-foreground lg:border-0"
                      )}
                    >
                      {on && <motion.span layoutId="prices-rail-mark" className="absolute -left-px top-1 hidden h-[calc(100%-8px)] w-px bg-brand lg:block" transition={{ duration: 0.3, ease: EASE }} aria-hidden />}
                      <span className="min-w-0 truncate">{r.label}</span>
                      {r.count ? <span className={cn("t-mono ml-auto hidden lg:inline", on ? "text-foreground/70" : "text-muted-foreground/70")}>{String(r.count).padStart(2, "0")}</span> : null}
                    </a>
                  </li>
                );
              })}
            </ol>
            <div className="mt-8 hidden border-t border-border pt-5 lg:block">
              <p className="text-[13.5px] leading-relaxed text-muted-foreground">Spørsmål om pris? Ring <a href={COMPANY.phoneHref} className="u-link tabular font-medium text-foreground" data-testid="public-prices-phone-link">{COMPANY.phone}</a> eller <button type="button" onClick={openChat} className="u-link font-medium text-foreground" data-testid="public-prices-rail-chat">spør assistenten</button>.</p>
            </div>
          </nav>
        </aside>

        {/* Groups */}
        <div className="min-w-0 lg:col-span-9">
          {PRICE_GROUPS.map((g, gi) => (
            <section key={g.id} id={g.id} ref={(el) => { sectionRefs.current[g.id] = el; }} className={cn("scroll-mt-24", gi > 0 && "mt-16 sm:mt-20")} data-testid={`price-group-${g.id}`}>
              <Reveal className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                  <p className="t-mono">{String(gi + 1).padStart(2, "0")}</p>
                  <h2 className="t-h2 mt-2">{g.title}</h2>
                </div>
                <p className="max-w-sm text-[15px] leading-relaxed text-muted-foreground sm:text-right">{g.text}</p>
              </Reveal>
              <ul className="mt-6 border-t border-border">
                {g.items.map((item, i) => <PriceItem key={item.id} item={item} index={i} />)}
              </ul>
            </section>
          ))}
          <p className="caption mt-8 max-w-2xl" data-testid="public-price-note">{PRICE_NOTE}</p>

          {/* How prices work */}
          <section id="slik" ref={(el) => { sectionRefs.current.slik = el; }} className="mt-20 scroll-mt-24 sm:mt-28" data-testid="public-prices-how">
            <Reveal>
              <Eyebrow>Slik set vi pris</Eyebrow>
              <h2 className="t-h2 mt-4 max-w-[20ch]">Fast pris der det går, tilbod der det trengst</h2>
            </Reveal>
            <ol className="mt-10 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              {HOW.map(({ icon: Icon, title, text }, i) => (
                <Reveal as="li" key={title} delay={i * 0.05} className="border-t border-border pt-5">
                  <div className="flex items-center justify-between">
                    <Icon className="h-6 w-6 text-foreground/70" {...ICON} />
                    <span className="t-mono">0{i + 1}</span>
                  </div>
                  <h3 className="mt-5 text-[17px] font-medium leading-snug">{title}</h3>
                  <p className="mt-2 max-w-[44ch] text-[15px] leading-relaxed text-muted-foreground">{text}</p>
                </Reveal>
              ))}
            </ol>

            <Reveal className="mt-14 grid gap-8 rounded-2xl bg-secondary/70 p-6 sm:p-8 lg:grid-cols-12 lg:gap-12" data-testid="public-prices-factors">
              <div className="lg:col-span-5">
                <p className="text-[16px] font-medium">Derfor står det «frå» på nokre prisar</p>
                <p className="mt-2 text-[15px] leading-relaxed text-muted-foreground">Prisen gjeld standardinstallasjon. Dette er det som oftast gjer at ein jobb blir større eller mindre:</p>
              </div>
              <ul className="grid gap-3 lg:col-span-7 sm:grid-cols-2">
                {FACTORS.map(([k, v]) => (
                  <li key={k} className="flex items-start gap-3 text-[15px] leading-snug">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-dot" aria-hidden />
                    <span><span className="font-medium">{k}</span> <span className="text-muted-foreground">{v}</span></span>
                  </li>
                ))}
              </ul>
            </Reveal>
          </section>
        </div>
      </section>

      {/* ---------- Larger work ---------- */}
      <section id="storre" ref={(el) => { sectionRefs.current.storre = el; }} className="grain relative mt-20 scroll-mt-24 overflow-hidden bg-ink py-16 text-white sm:mt-28 sm:py-24" data-testid="public-prices-large">
        <div className="container-public relative grid gap-12 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-6">
            <Eyebrow light>Større jobbar</Eyebrow>
            <h2 className="t-h2 mt-4 text-white">Prosjekt prisar vi etter synfaring og skriftleg tilbod</h2>
            <p className="mt-5 max-w-lg text-[16.5px] leading-relaxed text-white/65">Rehabilitering, hytte, næringsbygg, industri og samferdsel har sjeldan ein standard. Vi ser på anlegget, avklarer omfanget med deg og gir eit tilbod du kan ta stilling til.</p>
            <ul className="mt-10 divide-y divide-white/10 border-y border-white/10">
              {LARGE.map(({ icon: Icon, title, text, market }) => (
                <li key={title}>
                  <Link to={`/kontakt?gjeld=${market}`} className="group flex items-center gap-5 py-5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 rounded-md" data-testid={`public-prices-large-${market}`}>
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/10 text-white"><Icon className="h-5 w-5" {...ICON} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-[16.5px] font-medium">{title}</span>
                      <span className="block text-[14px] text-white/60">{text}</span>
                    </span>
                    <ArrowRight className="h-4 w-4 shrink-0 text-white/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-white" />
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Btn to="/kontakt" variant="light" size="lg" arrow data-testid="public-prices-befaring-button">Be om synfaring</Btn>
              <TextLink to="/prosjekt" light data-testid="public-prices-projects-link">Sjå prosjekt vi har gjort</TextLink>
            </div>
          </Reveal>
          <Reveal className="lg:col-span-6" delay={0.1}>
            <Photo src={IMG.ev_charger} alt="Heimeladar montert i garasje" ratio="aspect-[4/3] lg:aspect-[5/4]" dark sizes="(min-width: 1024px) 50vw, 100vw" />
          </Reveal>
        </div>
      </section>

      <div className="h-6 sm:h-10" aria-hidden />
    </div>
  );
}
